<!-- UI-7 C4: Projects rendered as placeholder cards (like the Image / Image-Group cards).
     Clicking the card opens the Project information pop-up (ProjectDetailModal) instead of
     navigating; "Open" is reachable from the pop-up. Projects have no thumbnail, so the card
     header is a branded placeholder with the project initial + the member-role badge. -->
<template>
<div class="card project-card">
  <!-- placeholder (the "thumbnail") opens the info pop-up; the name opens the project,
       mirroring the Images list (thumbnail → pop-up, name → viewer). -->
  <div class="card-image proj-placeholder" @click="$emit('open', project)" :title="$t('information')">
    <img v-if="imageUrl" class="proj-img" :src="imageUrl" :alt="project.name" @error="imageUrl = null">
    <img v-else class="proj-img" src="@/assets/project-placeholder.svg" alt="">
    <span class="proj-fade"></span>
    <span class="proj-role">
      <icon-project-member-role
        :is-manager="project.currentUserRoles && project.currentUserRoles.admin"
        :is-representative="project.currentUserRoles && project.currentUserRoles.representative"
      />
    </span>
    <span class="proj-info-badge"><i class="fas fa-info-circle"></i></span>
  </div>
  <div class="card-content">
    <p class="proj-name">
      <router-link :to="`/project/${project.id}`" :title="$t('button-open')">{{ project.name }}</router-link>
    </p>
    <div class="proj-meta">
      <span class="pm" :title="$t('images')"><i class="fas fa-images"></i> {{ project.numberOfImages }}</span>
      <span class="pm" :title="$t('members')"><i class="fas fa-users"></i> {{ project.membersCount }}</span>
      <span class="pm" :title="$t('user-annotations')"><i class="fas fa-draw-polygon"></i> {{ project.numberOfAnnotations }}</span>
    </div>
    <p class="proj-date" v-if="project.lastActivity">
      {{ $t('last-activity') }}: {{ Number(project.lastActivity) | moment('ll') }}
    </p>
  </div>
</div>
</template>

<script>
import IconProjectMemberRole from '@/components/icons/IconProjectMemberRole';
import {Ims, PropertyCollection} from '@/api';
import {get} from '@/utils/store-helpers.js';
import {PROJECT_IMAGE_KEY, resolveProjectImage} from '@/utils/project-image.js';

export default {
  name: 'project-preview',
  components: {IconProjectMemberRole},
  props: {
    project: {type: Object, required: true}
  },
  data() {
    return {
      // UI-9: optional custom project image (a URL, or an uploaded AttachedFile);
      // null → the template shows the branded placeholder image.
      imageUrl: null
    };
  },
  computed: {
    shortTermToken: get('currentUser/shortTermToken')
  },
  async created() {
    try {
      const props = (await PropertyCollection.fetchAll({object: this.project})).array;
      const prop = props.find(p => p.key === PROJECT_IMAGE_KEY);
      if (prop && prop.value) {
        this.imageUrl = resolveProjectImage(prop.value, Ims.instance.host, this.shortTermToken);
      }
    } catch (error) {
      // ignore — the placeholder image is shown
    }
  },
  mounted() {
    // UI-9b: update the card image immediately when it's changed from the edit popup (no reload).
    this.$eventBus.$on('project-image-updated', this.onImageUpdated);
  },
  beforeDestroy() {
    this.$eventBus.$off('project-image-updated', this.onImageUpdated);
  },
  methods: {
    onImageUpdated({projectId, value}) {
      if (projectId !== this.project.id) {
        return;
      }
      this.imageUrl = value
        ? resolveProjectImage(value, Ims.instance.host, this.shortTermToken)
        : null;
    }
  }
};
</script>

<style scoped lang="scss">
@import '@/assets/styles/colors.scss';

.project-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid $line-1;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: none;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: $primary;
    box-shadow: 0 6px 18px rgba(15, 17, 22, 0.08);
  }
}

.proj-placeholder {
  position: relative;
  height: 118px;
  overflow: hidden;
  background: $blue-soft;
  border-bottom: 1px solid $line-1;
  cursor: pointer;
}
.proj-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.2s ease;
}
.project-card:hover .proj-img { transform: scale(1.04); }
/* subtle bottom fade so the role/info badges stay legible over any image */
.proj-fade {
  position: absolute;
  inset: auto 0 0 0;
  height: 38px;
  background: linear-gradient(to bottom, rgba(15, 17, 22, 0) 0%, rgba(15, 17, 22, 0.10) 100%);
  pointer-events: none;
}
.proj-role {
  position: absolute;
  top: 6px;
  right: 8px;
}
.proj-info-badge {
  position: absolute;
  bottom: 6px;
  right: 8px;
  color: $primary;
  opacity: 0.55;
  font-size: 0.85rem;
}
.proj-placeholder:hover .proj-info-badge { opacity: 1; }

.card-content {
  padding: 0.7rem 0.85rem;
  flex: 1;
}
.proj-name {
  font-weight: 700;
  margin-bottom: 0.4rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  a {
    color: $primary;
    &:hover { color: $dark-primary; text-decoration: underline; }
  }
}
.proj-meta {
  display: flex;
  gap: 0.9rem;
  font-size: 0.8rem;
  color: $ink-3;

  .pm i { margin-right: 0.25rem; color: $ink-3; }
}
.proj-date {
  margin-top: 0.4rem;
  font-size: 0.72rem;
  color: $ink-3;
}
</style>
